import React, { useEffect, useState, useMemo } from 'react'
import { Modal, Form, Input, Select } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { Category } from '../types/category.types'
import categoryService from '../services/category.service'
import { DocumentUpload } from '@/components/DocumentUpload'

interface CategoryFormModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: any) => void
    initialValues?: Category | null
    isSubmitting?: boolean
    categories?: Category[]
}

export const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
    open,
    onCancel,
    onSubmit,
    initialValues,
    isSubmitting = false,
    categories: propCategories,
}) => {
    const [form] = Form.useForm()
    const [isImageUploading, setIsImageUploading] = useState(false)

    // Fetch categories if not passed as props
    const { data: fetchedCategories = [] } = useQuery({
        queryKey: ['categories'],
        queryFn: categoryService.getCategories,
        enabled: open && !propCategories,
    })

    const allCategories = propCategories || fetchedCategories

    // Filter out the category itself when editing to prevent circular hierarchy
    const parentOptions = useMemo(() => {
        return allCategories.filter((c) => !initialValues || String(c.id) !== String(initialValues.id))
    }, [allCategories, initialValues])

    useEffect(() => {
        if (open) {
            if (initialValues) {
                form.setFieldsValue({
                    name: initialValues.name,
                    slug: initialValues.slug,
                    parent_id: initialValues.parentId ? Number(initialValues.parentId) : undefined,
                    description: initialValues.description,
                    status: initialValues.status,
                    // Use imageId (UUID) for the upload component, not image (URL)
                    image: initialValues.imageId || undefined,
                })
            } else {
                form.resetFields()
                form.setFieldsValue({ status: 'active' })
            }
        }
    }, [open, initialValues, form])

    // Auto-generate slug from name
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const slug = e.target.value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
        form.setFieldValue('slug', slug)
    }

    const handleOk = () => {
        form.validateFields().then(onSubmit)
    }

    return (
        <Modal
            title={initialValues ? "Edit Category" : "Add New Category"}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={isSubmitting}
            okButtonProps={{ disabled: isImageUploading }}
            okText={isImageUploading ? 'Uploading image...' : initialValues ? 'Update Category' : 'Create Category'}
            width={600}
            destroyOnHidden
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Category Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter category name' }]}
                >
                    <Input placeholder="Enter category name" onChange={handleNameChange} />
                </Form.Item>
                <Form.Item
                    label="Slug"
                    name="slug"
                    rules={[{ required: true, message: 'Please enter slug' }]}
                >
                    <Input placeholder="e.g., building-materials" />
                </Form.Item>
                <Form.Item label="Parent Category" name="parent_id">
                    <Select
                        placeholder="Select parent category (optional)"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        {parentOptions.map((cat) => (
                            <Select.Option key={cat.id} value={Number(cat.id)}>
                                {cat.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Description" name="description">
                    <Input.TextArea rows={3} placeholder="Enter category description" />
                </Form.Item>
                <Form.Item label="Category Image" name="image">
                    <DocumentUpload documentType="category_image" onUploadingChange={setIsImageUploading} />
                </Form.Item>
                <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true }]}
                >
                    <Select>
                        <Select.Option value="active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    )
}
